import { Account, UserType } from '@/types';

/**
 * Authentication method types for user display
 */
export type AuthMethod = 'sso' | 'local';

/**
 * Internal provenance tags stamped on hunt/agent-filed risks. These are
 * implementation signals (hunt routing, Cato re-validation, leaderboard
 * accounting, agora re-triage skip, HackerOne ack gating, Helix eligibility)
 * — they reveal internal triage machinery and must not be shown to customers.
 * Mirrors the tag sets written in
 * backend/pkg/lib/agent/tools/evidence/filing.go (persistFinding: 'hunt',
 * 'agent-reported') and backend/pkg/lib/agent/tools/propose_risk.go /
 * reject_risk.go / update_risk.go ('cato-proposed', 'cato-reviewed').
 */
export const INTERNAL_PROVENANCE_TAGS = [
  'hunt',
  'agent-reported',
  'cato-proposed',
  'cato-reviewed',
] as const;

/**
 * Internal provenance tags hidden from EVERY viewer, Praetorian included.
 * The cato triage tags moved here in ENG-4581; hunt/agent-reported joined them
 * in ENG-4604 (product decision 2026-07-17: these are internal triage/
 * provenance signals that must not surface anywhere in the UI). Operators keep
 * their triage path via the raw persisted tags/origin — masking is read-time
 * only, so the Hunt view, the `hunt` origin, and isHuntFinding-driven UI are
 * unaffected; only the visible chip/filter goes away. The raw tags stay
 * persisted as load-bearing plumbing for the backend consumers (hunt routing,
 * Cato re-validation, agora re-triage skip, HackerOne ack gating, Helix
 * eligibility, leaderboard). Mirrors alwaysHiddenTags in
 * backend/pkg/lib/provenance/provenance.go.
 */
const ALWAYS_HIDDEN_PROVENANCE_TAGS = [
  'hunt',
  'agent-reported',
  'cato-proposed',
  'cato-reviewed',
] as const;

/**
 * Lowercased lookup sets, built once at module scope since the source arrays
 * are static.
 */
const HIDDEN_INTERNAL_TAGS = new Set<string>(
  INTERNAL_PROVENANCE_TAGS.map(tag => tag.toLowerCase())
);
const ALWAYS_HIDDEN_TAGS = new Set<string>(
  ALWAYS_HIDDEN_PROVENANCE_TAGS.map(tag => tag.toLowerCase())
);

/**
 * History-author identities stamped by the backend on machine-authored history
 * entries. Both are wire-contract strings matched verbatim against `history.by`
 * — do not alter their casing or spacing.
 *
 * - `PRAETORIAN_AI_HISTORY_AUTHOR` ("Praetorian AI") — the ML auto-triage /
 *   affiliation prediction model (backend `model.HistoryAuthorPraetorianAI`).
 * - `PRAETORIAN_GUARD_HISTORY_AUTHOR` ("Praetorian Guard") — the Praetorian
 *   Guard validation agent (cato / HackerOne), which stamps agent-validated
 *   risks (backend `model.HistoryAuthorPraetorianGuard`). Both are applied via
 *   `WithHistoryAuthor`.
 */
export const PRAETORIAN_AI_HISTORY_AUTHOR = 'Praetorian AI';
export const PRAETORIAN_GUARD_HISTORY_AUTHOR = 'Praetorian Guard';

/** True when a history entry was authored by a Praetorian machine actor —
 * either the ML auto-triage/affiliation model ("Praetorian AI") or the
 * Praetorian Guard validation agent ("Praetorian Guard"). Mirrors the backend
 * model.HistoryAuthorPraetorianAI / model.HistoryAuthorPraetorianGuard split. */
export const isMachineHistoryAuthor = (by?: string): boolean =>
  by === PRAETORIAN_AI_HISTORY_AUTHOR || by === PRAETORIAN_GUARD_HISTORY_AUTHOR;

/**
 * Strips internal agent/hunt tags (e.g., "[cato-agent]", "[hunt]") from the
 * beginning of comment text. Strips tokens whose content ends with "-agent",
 * is exactly "agent", or is the hunt-provenance "[hunt]" prefix.
 * Multiple consecutive leading tags are all stripped.
 * Only applied for non-Praetorian viewers.
 */
export function stripAgentTags(
  text: string | null | undefined,
  userType: UserType
): string {
  if (text == null) return '';
  if (userType === 'praetorian') return text;
  return text.replace(/^(?:\[(?:[\w-]*agent|hunt)\]\s*)+/gi, '');
}

/**
 * Removes internal provenance tags from a tag list. Non-Praetorian viewers get
 * the full INTERNAL_PROVENANCE_TAGS set removed; Praetorian viewers get the
 * ALWAYS_HIDDEN_PROVENANCE_TAGS set removed — which, since ENG-4604, is the
 * same four provenance tags, so no viewer sees hunt/agent-reported/cato-* on a
 * tag surface. Customer-added tags are preserved for everyone.
 */
export function filterInternalTags(
  tags: readonly string[] | null | undefined,
  userType: UserType
): string[] {
  if (!tags) return [];
  const hidden =
    userType === 'praetorian' ? ALWAYS_HIDDEN_TAGS : HIDDEN_INTERNAL_TAGS;
  return tags.filter(tag => !hidden.has(tag.toLowerCase()));
}

/**
 * Determines if an email belongs to a Praetorian employee
 */
export function isPraetorianEmail(email: string): boolean {
  // Defensive check: handle null/undefined/non-string inputs
  if (!email || typeof email !== 'string') {
    return false;
  }

  return (
    email.endsWith('@praetorian.com') || email.endsWith('@praetorianlabs.email')
  );
}

/**
 * Masks Praetorian employee names for customer viewing
 * Praetorian users see full names, customers see "The Praetorian Guard"
 */
export function maskPraetorianName(
  email: string,
  displayName: string | undefined,
  userType: UserType
): string {
  // If viewer is Praetorian, show real name or email
  if (userType === 'praetorian') {
    return displayName || email;
  }

  // If author is Praetorian and viewer is customer, mask the name
  if (isPraetorianEmail(email)) {
    return 'The Praetorian Guard';
  }

  // Show customer names/emails normally
  return displayName || email;
}

/**
 * Extracts configured SSO domains from SSO account entries
 * SSO accounts have member field starting with 'sso:' (e.g., 'sso:example.com')
 */
export function getSSODomains(accounts: Account[]): string[] {
  if (!accounts || !Array.isArray(accounts)) {
    return [];
  }

  return accounts
    .filter(account => account.member?.startsWith('sso:'))
    .map(account => account.member.split(':')[1])
    .filter(Boolean);
}

/**
 * Determines the authentication method for a user based on their email
 * and the configured SSO domains for the account
 *
 * @param userEmail - The email address of the user
 * @param ssoDomains - List of configured SSO domains for the account
 * @returns 'sso' if user's domain matches an SSO domain, 'local' otherwise
 */
export function getAuthMethod(
  userEmail: string,
  ssoDomains: string[]
): AuthMethod {
  // Defensive check
  if (!userEmail || typeof userEmail !== 'string') {
    return 'local';
  }

  // Praetorian users always use local auth (Google SSO for internal, not customer SSO)
  if (isPraetorianEmail(userEmail)) {
    return 'local';
  }

  // No SSO configured means local auth
  if (!ssoDomains || ssoDomains.length === 0) {
    return 'local';
  }

  // Extract domain from email
  const emailDomain = userEmail.split('@')[1]?.toLowerCase();
  if (!emailDomain) {
    return 'local';
  }

  // Check if user's email domain matches any configured SSO domain
  const isSSO = ssoDomains.some(
    ssoDomain => ssoDomain.toLowerCase() === emailDomain
  );

  return isSSO ? 'sso' : 'local';
}
